import { useStoreLogger } from '@/logger.ts'
import type {
  IAoeCustomer,
  IAoeRegistration,
  IAoeRegistrationResponse,
  ICustomerResponse
} from '@/types/CustomerTypes.ts'
import { CustomerGet, AccountCreate } from '@/api/Customer.ts'
import { GetBeeNumberAuthenicatedUser } from '@/stores/SettingsStore'

export const CustomerLocalStorageKey = 'AoeCustomer'

// Reinstatement kits are only for someone who recently went from consultant back to customer. The backend
// decides eligibility (AOEEntityBO, the same CheckStatus that makes the kit order a reinstatement), so a
// guest or a logged-in customer who is not eligible must never be offered them.
export const REINSTATEMENT_PRODUCT_CODES = ['RS', 'RSDE', 'RSFR']

export async function FilterReinstatementKits<T extends { productCode: string }>(products: T[]): Promise<T[]> {
  const beeNumber = await GetBeeNumberAuthenicatedUser()
  let reinstatementEligible = false
  if (beeNumber) {
    const customer = await GetCustomerFromLocalStorageOrApi(beeNumber)
    reinstatementEligible = customer?.reinstatementEligible ?? false
  }
  if (reinstatementEligible) return products
  return products.filter(p => !REINSTATEMENT_PRODUCT_CODES.includes(p.productCode))
}

export function GetCustomerFromLocalStorage(
  beeNumberCustomer: number
): IAoeCustomer | null {
  const logger = useStoreLogger()
  logger.debug('GetCustomerFromLocalStorage', beeNumberCustomer)
  const key = CustomerLocalStorageKey
  const json = localStorage.getItem(key)
  if (json) {
    logger.debug('Customer found in local storage')
    const cust: IAoeCustomer = JSON.parse(json)
    if (cust.beeNumber === beeNumberCustomer) {
      return cust
    }
  }
  logger.debug('Customer not found in local storage')
  return null
}

export async function GetCustomerFromLocalStorageOrApi(
  beeNumberCustomer: number
): Promise<IAoeCustomer | null> {
  const logger = useStoreLogger()
  logger.debug('GetCustomerFromLocalStorageOrApi', beeNumberCustomer)

  let cust = GetCustomerFromLocalStorage(beeNumberCustomer)
  if (cust) return cust

  const custResponse = await CustomerGet(beeNumberCustomer)
  if (custResponse) {
    SaveCustomerToLocalStorage(custResponse)
    cust = GetCustomerFromLocalStorage(beeNumberCustomer)
  }
  return cust
}

export function SaveCustomerToLocalStorage(customerResponse: ICustomerResponse) {
  const newValue = JSON.stringify(customerResponse.item)
  const logger = useStoreLogger()
  logger.debug('SaveCustomerToLocalStorage', newValue)
  const key = CustomerLocalStorageKey
  localStorage.setItem(key, newValue)
  window.dispatchEvent(
    new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
  )
}

export async function CreateAccount(
  request: IAoeRegistration
): Promise<IAoeRegistrationResponse | null> {
  const logger = useStoreLogger()
  logger.debug('CreateAccount', request)

  const response = await AccountCreate(request)
  return response
}
